import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, FormControl, Grid } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import logoNav2 from '../../../assets/images/logo-nav2.svg';
import { InputText } from '../../../components/Forms/InputText';
import Loader from '../../../components/Loader/Loader';
import { PublicRoute } from '../../../config/Constants';
import { useCustomStore, UseFocus, useMountEffect } from '../../../hooks';
import { CreatePasswordValidationInput } from '../../../types';
import { SnackBarConfig } from '../../../utils/SnackBarConfig';
import { CreatePasswordSchema } from '../../../utils/ValidatorSchema';
import useStyles from '../PublicStyles';

const CreatePassword: React.FC = () => {
  const classes = useStyles();
  const { apiStore } = useCustomStore();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const resetToken = searchParams.get('token') || '';
  const [loading, setLoading] = useState(false);

  // validation
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreatePasswordValidationInput>({
    criteriaMode: 'all',
    defaultValues: {
      password: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(CreatePasswordSchema),
  });

  const submitCreatePassword = useCallback(
    async (data: CreatePasswordValidationInput) => {
      setLoading(true);
      const createPasswordResp = await apiStore.userApi.createPassword({
        newPassword: data.password,
        token: resetToken,
      });
      if (createPasswordResp.isOk()) {
        enqueueSnackbar(createPasswordResp.value, SnackBarConfig('s'));
        navigate(PublicRoute.LOGIN);
      } else {
        enqueueSnackbar(
          String(createPasswordResp.error.message),
          SnackBarConfig('e'),
        );
        setLoading(false);
      }
    },
    [resetToken, apiStore.userApi, enqueueSnackbar, navigate],
  );

  const [input1Ref, setInput1Focus] = UseFocus();

  useMountEffect(setInput1Focus);

  return (
    <Grid
      container
      spacing={0}
      direction={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      className={classes.sliderHome}>
      <Grid item margin={'auto'}>
        <div className={classes.rectangle}>
          <img src={logoNav2} className={classes.logoNav2} />
          <form
            style={{
              width: '100%',
            }}
            onSubmit={handleSubmit(submitCreatePassword)}>
            <Grid
              sx={{
                marginTop: '31px',
              }}>
              <FormControl error={false} fullWidth>
                <Grid container>
                  <Box className={classes.labels}>Enter New Password</Box>
                  <Grid item md={12} sm={12} xs={12}>
                    <Controller
                      control={control}
                      defaultValue=""
                      name="password"
                      render={({
                        field: { onChange, value, name, ref },
                        formState,
                      }) => {
                        return (
                          <InputText
                            isFormSubmittedOnce={!!formState.submitCount}
                            inputRefObj={input1Ref}
                            errors={errors.password?.message || ''}
                            name={name}
                            isRefreshValue={true}
                            value={value}
                            onChangeText={onChange}
                            isSecureTextEntry={true}
                            isEditable={false}
                            className={classes.inputStyles}
                            type={'password'}
                            variant="filled"
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
              </FormControl>
            </Grid>
            <Button
              disabled={loading}
              tabIndex={0}
              type="submit"
              variant="contained"
              fullWidth
              className={classes.buttonStyles}>
              {loading ? (
                <Loader
                  className={'flex-centering mt-1em mb-1em'}
                  style={{ marginBottom: 16, marginTop: 16 }}
                  color="primary"
                />
              ) : (
                'Submit'
              )}
            </Button>
          </form>
        </div>
      </Grid>
    </Grid>
  );
};

export default observer(CreatePassword);
